import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, Image as BaseImage, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import BaseLink from '@amblique/base/app/components/link';
import {useResponsiveValue} from '@amblique/base/app/hooks/use-responsive-value';
import {inflector} from '@amblique/base/shared/helpers/inflector';
import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';

import {resolveContentfulLinkTargetProps} from '@amblique/king-living/app/contentful/helpers/resolve-contentful-link-url';
import {buildContentfulImageAttributes} from '@amblique/king-living/app/utils/contentful-image-src';

/**
 * Contentful Image `width` / `height` may be a number, a responsive string
 * (`mobile: 320, desktop: 800`), or already an object. `useResponsiveValue` only
 * parses strings (and passes objects through) — coerce numbers so CMS dims are not dropped.
 *
 * @param {unknown} value
 * @returns {unknown}
 */
function normalizeResponsiveDimension(value) {
  if (value == null || value === '') {
    return value;
  }

  if (typeof value === 'object') {
    return value;
  }

  return String(value);
}

/**
 * Image component
 *
 * Builds Contentful Images API URLs (`w`/`h`/`q`/`fm`/`fit`/`f`) and emits
 * responsive `srcSet`/`sizes` (TSD `sw`/`sm` helpers expanded in JS). See
 * `contentful-image-src.js` and KL-1133.
 *
 * @param {Object} props - Component props
 * @param {Contentful.Image} props.component - Image component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {string} [props.sizes] - Optional `sizes` override for layout-aware callers
 * @param {string|number[]|number} [props.srcSetWidths] - Optional TSD `sw` override
 * @param {string|number[]|number} [props.srcSetMultipliers] - Optional TSD `sm` override
 * @returns {React.ReactElement}
 */
export const Image = ({
  component,
  isComplete = true,
  isFetching = false,
  loading = 'lazy',
  fetchpriority,
  sizes: sizesProp,
  srcSetWidths: srcSetWidthsProp,
  srcSetMultipliers: srcSetMultipliersProp,
  ...props
}) => {
  if (!isComplete && isFetching) {
    return null;
  }

  const {
    mobileAsset,
    tabletAsset,
    desktopAsset,
    altText,
    url,
    openInNewTab,
    fit,
    focus,
    format,
    description,
    imageDescriptionContent,
  } = component;

  // Optional CMS fields (forward-compatible if/when added to the Image content type).
  const srcSetWidths = srcSetWidthsProp ?? component.srcSetWidths ?? component.sw;
  const srcSetMultipliers = srcSetMultipliersProp ?? component.srcSetMultipliers ?? component.sm;
  const sizesOverride = sizesProp ?? component.sizes;

  const {style: propsStyle, ...restProps} = props;

  const styles = useMultiStyleConfig('contentful/image', {component, isComplete, isFetching});
  const assetUrl = useMemo(() => {
    const urls = [];

    if (mobileAsset?.url) {
      urls.push(`mobile: ${mobileAsset.url}`);
    } else if (tabletAsset?.url) {
      urls.push(`mobile: ${tabletAsset.url}`);
    } else if (desktopAsset?.url) {
      urls.push(`mobile: ${desktopAsset.url}`);
    }

    if (tabletAsset?.url && mobileAsset?.url) {
      urls.push(`tablet: ${tabletAsset.url}`);
    }

    if (desktopAsset?.url && (tabletAsset?.url || mobileAsset?.url)) {
      urls.push(`desktop: ${desktopAsset.url}`);
    }

    if (urls.length === 0) {
      return '';
    }

    // Return responsive string format
    return urls.join(', ');
  }, [mobileAsset, tabletAsset, desktopAsset]);

  // Parse responsive values to get current matched values
  const width = useResponsiveValue(normalizeResponsiveDimension(component.width), 'value', 'number');
  const height = useResponsiveValue(normalizeResponsiveDimension(component.height), 'value', 'number');
  const quality = useResponsiveValue(component.quality, 'value');
  const imageUrl = useResponsiveValue(assetUrl, 'value', 'string');

  // Intrinsic pixel dimensions of the asset actually rendered at this breakpoint.
  // Provided by the king-living override of the base-image fragment (adds
  // width/height to each responsive asset). Used as a fallback so we can still
  // emit explicit width/height when the author left the entry-level dims blank.
  const intrinsic = useMemo(() => {
    const match = [mobileAsset, tabletAsset, desktopAsset].find(
      (asset) => asset?.url && imageUrl === asset.url
    );

    return match?.width && match?.height ? {w: match.width, h: match.height} : null;
  }, [mobileAsset, tabletAsset, desktopAsset, imageUrl]);

  const {src, srcSet, sizes} = useMemo(() => {
    const focusValue =
      focus && fit && (fit === 'fill' || fit === 'crop' || fit === 'thumb')
        ? inflector.tableize(focus)
        : undefined;

    return buildContentfulImageAttributes({
      imageUrl,
      width,
      height,
      quality,
      format,
      fit,
      focus: focusValue,
      authoredWidth: component.width,
      srcSetWidths,
      srcSetMultipliers,
      sizes: sizesOverride,
      maxWidth: intrinsic?.w,
    });
  }, [
    imageUrl,
    width,
    height,
    quality,
    format,
    fit,
    focus,
    component.width,
    srcSetWidths,
    srcSetMultipliers,
    sizesOverride,
    intrinsic,
  ]);

  const caption =
    imageDescriptionContent?.json ? (
      <Box className="image__description" {...styles.description}>
        <RichTextContent content={imageDescriptionContent} />
      </Box>
    ) : description ? (
      <Text className="image__description" {...styles.description}>{description}</Text>
    ) : null;

  const imageLink = resolveContentfulLinkTargetProps(url);
  // Emit explicit width/height so the browser can reserve layout space and reduce
  // CLS. Prefer the author-configured display dimensions; when only one (or none)
  // is set, derive the missing value(s) from the rendered asset's intrinsic ratio
  // so we still reserve space with a correct aspect ratio. Chakra maps
  // htmlWidth/htmlHeight to the native width/height attributes.
  const {htmlWidth, htmlHeight} = useMemo(() => {
    if (width && height) {
      return {htmlWidth: width, htmlHeight: height};
    }

    if (intrinsic) {
      if (width) {
        return {htmlWidth: width, htmlHeight: Math.round((width * intrinsic.h) / intrinsic.w)};
      }

      if (height) {
        return {htmlWidth: Math.round((height * intrinsic.w) / intrinsic.h), htmlHeight: height};
      }

      return {htmlWidth: intrinsic.w, htmlHeight: intrinsic.h};
    }

    return {htmlWidth: undefined, htmlHeight: undefined};
  }, [width, height, intrinsic]);
  const imageNode = (
    <BaseImage
      alt={altText}
      fetchpriority={fetchpriority}
      htmlHeight={htmlHeight}
      htmlWidth={htmlWidth}
      loading={loading}
      sizes={sizes || undefined}
      src={src}
      srcSet={srcSet || undefined}
      {...styles.image}
    />
  );

  // Wrap only the image in Link so caption/description does not inherit link underline styles.
  // Expose author width as a CSS variable so Media Carousel (and other) themes can honor
  // per-image CMS width while keeping their own fixed max-height / default dimensions.
  const displayWidthPx = Number(width);
  const rootStyle = {
    ...propsStyle,
    ...(Number.isFinite(displayWidthPx) && displayWidthPx > 0
      ? {'--kl-image-display-width': `${displayWidthPx}px`}
      : null),
  };

  return (
    <Box {...styles.root} {...restProps} style={rootStyle}>
      {imageLink.linkUrl ? (
        <BaseLink
          className="image-link"
          href={imageLink.href}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          target={openInNewTab ? '_blank' : '_self'}
          to={imageLink.to}
        >
          {imageNode}
        </BaseLink>
      ) : (
        imageNode
      )}
      {caption}
    </Box>
  );
};

Image.displayName = 'Image';
Image.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,

  /** Native img loading strategy. Defaults to "lazy"; above-the-fold callers (e.g. hero) pass "eager". */
  loading: PropTypes.oneOf(['lazy', 'eager']),

  /** Native img fetchpriority. Above-the-fold/LCP callers may pass "high". */
  fetchpriority: PropTypes.oneOf(['high', 'low', 'auto']),

  /** Optional `sizes` override when the layout slot is known (hero vs tile vs full-bleed). */
  sizes: PropTypes.string,

  /** Optional TSD `sw` override — comma-separated or array of srcset widths. */
  srcSetWidths: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.number), PropTypes.number]),

  /** Optional TSD `sm` override — DPR multipliers (default 1,2,3). */
  srcSetMultipliers: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.number), PropTypes.number]),
};
